import { ResourceDetail, ResourceDetailsUpdate } from '@cibo/core'
import { Markdown, ResourceDetailFeatureTaskEditorProps } from '@cibo/ui'
import { Collapse, Stack, Typography } from '@mui/material'
import { assocPath, path, pathEq, propEq } from 'ramda'
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useBatchedFields, useUpdateMultipleFields } from '../../../queries'
import { BooleanRollupRowTable } from '../BooleanRollupRowTable'
import { BooleanRollupInput, BULK_ANSWERS, BulkAnswer } from './BooleanRollupInput'

export type BooleanRollupProps<T extends ResourceDetail = ResourceDetail> = {
  detailKey?: keyof T['input'] | keyof T['result']
  ns: string
  answerValues?: Partial<Record<BulkAnswer, { value: any }>>
  showTableAnswers?: BulkAnswer[]
  rollupAnswers?: BulkAnswer[]
} & ResourceDetailFeatureTaskEditorProps<T>

export const BooleanRollup = <T extends ResourceDetail = ResourceDetail>(
  props: BooleanRollupProps<T>
) => {
  const {
    answerValues = { yes: { value: 'yes' }, no: { value: 'no' }, unknown: { value: 'unknown' } },
    detailRequirements: [{ traitId, year, resultsOnly }],
    ns,
    detailKey,
    onError,
    onSuccess,
    onUpdating,
    resourceIds,
    userRole,
    ownerName,
    showTableAnswers = ['yes'],
    rollupAnswers = ['no', 'unknown'],
  } = props
  const { t } = useTranslation('@cibo/landmanager/BooleanRollup')

  const fieldModels = useBatchedFields(resourceIds)

  const [rollupAnswer, setRollupAnswer] = useState<BulkAnswer>()
  const updateFields = useUpdateMultipleFields()

  const valuePath = useMemo(() => {
    const valuePath: Array<string> = [resultsOnly ? 'result' : 'input']
    if (detailKey) valuePath.push(detailKey as string)
    return valuePath
  }, [detailKey, resultsOnly])

  const detailHasAnswer = useCallback(
    (answer: BulkAnswer) => pathEq(valuePath, answerValues[answer]?.value),
    [valuePath, answerValues]
  )

  const answerForDetail = useCallback(
    (detail: ResourceDetail) => {
      if (!answerValues) return
      const value = path(valuePath, detail)
      if (typeof value === 'undefined') return

      for (const answer of BULK_ANSWERS) {
        if (value === answerValues[answer]?.value) return answer
      }
    },
    [answerValues, valuePath]
  )

  const details = fieldModels.data?.map(field => field.resolveStandingDetail(traitId, year))

  const existingDetails = details?.filter(Boolean)
  const immutableDetails = existingDetails?.filter(detail => detail.immutable)

  // handle first fetch of fields
  // TODO: update dependency array to include appropriate depdendencies and ensure this
  //       runs only when fields is fetched for the first time.
  useEffect(() => {
    if (!fieldModels.isFetched || !fieldModels.data) return

    if (!existingDetails) return
    const allDetailsPresent = existingDetails?.length === resourceIds.length

    if (allDetailsPresent) {
      for (const rollupAnswer of rollupAnswers) {
        // if all details have rollup answer
        if (existingDetails?.every(detailHasAnswer(rollupAnswer))) {
          setRollupAnswer(rollupAnswer)
          return
        }
      }

      const answers = new Set<BulkAnswer>()
      existingDetails.forEach(detail => {
        answers.add(answerForDetail(detail) as BulkAnswer)
      })
    }

    // some answer is not the rollup
    for (const detail of existingDetails) {
      const answer = answerForDetail(detail)
      if (answer && !rollupAnswers.includes(answer)) {
        setRollupAnswer(answer)
        return
      }
    }
  }, [fieldModels.isFetched])

  const syncAnswers = (answers: Array<{ resourceId: string; answer: BulkAnswer }>) => {
    if (!fieldModels.isFetched) return

    const template = { traitId, year }

    const updates: ResourceDetailsUpdate[] = []

    answers.forEach(({ resourceId, answer }) => {
      const fieldModel = fieldModels.data?.find(propEq('resourceId', resourceId))
      if (!fieldModel) return

      const detail = fieldModel?.findDetail({ traitId, year })

      if (detail?.immutable) return

      if (!detailHasAnswer(answer)(detail)) {
        updates.push({
          resourceId: fieldModel.resourceId,
          details: [assocPath(valuePath, answerValues?.[answer]?.value, template)],
        })
      }
    })

    if (updates.length === 0) return

    onUpdating?.()
    updateFields
      .mutateAsync(updates)
      .then(() => onSuccess?.())
      .catch(onError)
  }

  const handleClickRollupAnswer = (newAnswer: BulkAnswer) => () => {
    if (!fieldModels.data) return

    if (fieldModels.data.length === 1 || rollupAnswers.includes(newAnswer)) {
      syncAnswers(fieldModels.data.map(({ resourceId }) => ({ resourceId, answer: newAnswer })))
    }

    setRollupAnswer(newAnswer)
  }

  const allImmutable = useMemo(() => {
    return fieldModels.data?.every(({ details }) =>
      details.some(detail => detail.traitId === traitId && detail.year === year && detail.immutable)
    )
  }, [fieldModels?.dataUpdatedAt])

  const showTable =
    rollupAnswer && showTableAnswers.includes(rollupAnswer) && (fieldModels.data?.length ?? 0) > 1

  // disable rollup buttons if some immutable detail disagrees (can't be rolled up)
  const buttonDisabled = rollupAnswers.reduce(
    (acc, rollupAnswer) => ({
      ...acc,
      [rollupAnswer]: immutableDetails?.some(detail => !detailHasAnswer(rollupAnswer)(detail)),
    }),
    {}
  )

  return (
    <Stack spacing={3} flex={1}>
      <Markdown
        overrides={{
          em: {
            component: ({ children }: { children: ReactNode }) => (
              <Typography variant="body2" color="text.secondary" component="span">
                {children}
              </Typography>
            ),
          },
        }}
      >
        {t('question', { context: userRole, name: ownerName, ns, count: fieldModels.data?.length })}
      </Markdown>

      <Stack>
        <BooleanRollupInput
          answer={rollupAnswer}
          answerValues={answerValues}
          buttonDisabled={buttonDisabled}
          disabled={!fieldModels.isFetched || allImmutable || updateFields.isPending}
          onChange={handleClickRollupAnswer}
        />

        <Collapse in={showTable}>
          <Stack mt={3} spacing={3}>
            <Markdown>{t('tableCta', { context: userRole, name: ownerName, ns })}</Markdown>
            <BooleanRollupRowTable {...props} />
          </Stack>
        </Collapse>
      </Stack>
    </Stack>
  )
}
